import dayjs from 'dayjs';
import { MonthsDetails } from './constants';
/**
 * Get the next 5 upcoming months starting from next month (current month is covered by ASAP)
 */
export function getUpcomingMonths() {
    const currentDate = dayjs();
    const months = [];
    for (let i = 1; i <= 5; i++) {
        const futureDate = currentDate.add(i, 'month');
        const monthNumber = futureDate.month() + 1; // dayjs months are 0-indexed
        const year = futureDate.year();
        // Find the month details from our constants
        const monthDetails = Object.values(MonthsDetails).find(m => m.number === monthNumber);
        if (monthDetails) {
            // Pre-calculate the min and max dates for this month
            const minDate = futureDate.startOf('month').format('YYYY-MM-DD');
            const maxDate = futureDate.endOf('month').format('YYYY-MM-DD');
            months.push({
                key: monthDetails.short.toLowerCase(),
                label: monthDetails.name,
                monthNumber,
                year,
                minDate,
                maxDate,
            });
        }
    }
    return months;
}
/**
 * Get the last day of the current month
 */
export function getCurrentMonthEnd() {
    return dayjs().endOf('month').format('YYYY-MM-DD');
}
/**
 * Get month details from month key using upcoming months
 * @param monthKey - Month abbreviation in lowercase
 */
function getMonthFromKey(monthKey) {
    const upcomingMonths = getUpcomingMonths();
    return upcomingMonths.find(m => m.key === monthKey);
}
function normalizeSelections(selections) {
    const currentMonthKey = dayjs().format('MMM').toLowerCase();
    // If current month is selected, treat it as ASAP (since we don't show current month as an option)
    const normalized = selections.map(s => (s === currentMonthKey ? 'asap' : s));
    // Dedupe while preserving order
    return Array.from(new Set(normalized));
}
/**
 * Calculate min and max expected move-in dates based on selections
 * @param selections - Array of selected move-in date options
 * @returns Object with min and max date strings, or null if no valid selection
 */
export function calculateExpectedMoveInDates(selections) {
    const normalizedSelections = normalizeSelections(selections || []);
    if (!normalizedSelections || normalizedSelections.length === 0) {
        return { min: null, max: null };
    }
    const hasAsap = normalizedSelections.includes('asap');
    const monthSelections = normalizedSelections.filter(s => s !== 'asap');
    let min = null;
    let max = null;
    // Calculate min date
    if (hasAsap) {
        // If ASAP is selected, min is always null (immediate availability)
        min = null;
    }
    else if (monthSelections.length > 0) {
        // Get min dates from selected months and find the earliest
        const monthDates = monthSelections
            .map(s => getMonthFromKey(s))
            .filter(Boolean)
            .map(month => month.minDate);
        if (monthDates.length > 0) {
            min = monthDates.sort()[0] || null;
        }
    }
    // Calculate max date
    if (hasAsap && monthSelections.length === 0) {
        // Only ASAP selected: max = end of current month
        max = getCurrentMonthEnd();
    }
    else if (monthSelections.length > 0) {
        // Get max dates from selected months and find the latest
        const monthDates = monthSelections
            .map(s => getMonthFromKey(s))
            .filter(Boolean)
            .map(month => month.maxDate);
        if (monthDates.length > 0) {
            max = monthDates.sort().reverse()[0] || null;
        }
    }
    else if (hasAsap) {
        // Only ASAP selected (fallback)
        max = getCurrentMonthEnd();
    }
    return { min, max };
}
/**
 * Calculate selections from min and max expected move-in dates
 * @param minDate - Minimum expected move-in date string (YYYY-MM-DD) or null
 * @param maxDate - Maximum expected move-in date string (YYYY-MM-DD) or null
 * @returns Array of MoveInDateSelection options that would produce these dates
 */
export function calculateExpectedMoveInSelections(minDate, maxDate) {
    // If no dates provided, return empty selections
    if (!minDate && !maxDate) {
        return [];
    }
    const selections = [];
    const upcomingMonths = getUpcomingMonths();
    // Check for ASAP scenario
    if (!minDate && maxDate) {
        // If min is null but max is set, it indicates ASAP is involved
        const currentMonthEnd = getCurrentMonthEnd();
        if (maxDate === currentMonthEnd) {
            // Only ASAP selected
            return ['asap'];
        }
        else {
            // ASAP + specific months
            selections.push('asap');
        }
    }
    // Check which months match the date ranges using pre-calculated dates
    if (minDate || maxDate) {
        for (const month of upcomingMonths) {
            // Use pre-calculated minDate and maxDate from the month object
            const { minDate: monthStart, maxDate: monthEnd } = month;
            // Check if this month is included in the range
            let monthIncluded = false;
            if (minDate && maxDate) {
                // Both dates provided - check if month overlaps with range
                monthIncluded = monthEnd >= minDate && monthStart <= maxDate;
            }
            else if (minDate) {
                // Only min date - check if month starts after or on min date
                monthIncluded = monthStart >= minDate;
            }
            else if (maxDate) {
                // Only max date - check if month ends before or on max date
                monthIncluded = monthEnd <= maxDate;
            }
            if (monthIncluded) {
                selections.push(month.key);
            }
        }
    }
    return selections;
}
/**
 * Get display label for selected move-in dates
 * @param selections - Array of selected move-in date options
 */
export function getMoveInDateDisplayLabel(selections) {
    const normalizedSelections = normalizeSelections(selections || []);
    if (!normalizedSelections || normalizedSelections.length === 0) {
        return undefined;
    }
    const hasAsap = normalizedSelections.includes('asap');
    const monthSelections = normalizedSelections.filter(s => s !== 'asap');
    if (hasAsap && monthSelections.length === 0) {
        // Only ASAP selected
        return 'As soon as possible';
    }
    else if (!hasAsap && monthSelections.length === 1) {
        // Single month selected
        const monthDetails = Object.values(MonthsDetails).find(m => m.short.toLowerCase() === monthSelections[0]);
        return monthDetails?.name || monthSelections[0];
    }
    else if (hasAsap && monthSelections.length === 1) {
        // ASAP + one month
        const monthDetails = Object.values(MonthsDetails).find(m => m.short.toLowerCase() === monthSelections[0]);
        return `ASAP or ${monthDetails?.name || monthSelections[0]}`;
    }
    else {
        // Multiple selections
        const totalCount = selections.length;
        if (hasAsap) {
            return `ASAP + ${monthSelections.length} month${monthSelections.length === 1 ? '' : 's'}`;
        }
        else {
            return `${totalCount} month${totalCount === 1 ? '' : 's'}`;
        }
    }
}
